import type { ComponentType, SVGProps } from 'react';
import { InterventionIcon } from '../api/generated';
import ClipboardIcon from '../assets/icons/clipboard.svg?react';
import ClinicIcon from '../assets/icons/clinic.svg?react';
import HomeIcon from '../assets/icons/home.svg?react';
import BedIcon from '../assets/icons/bed.svg?react';
import GlassesIcon from '../assets/icons/pre-examination.svg?react';
import HeadsetIcon from '../assets/icons/call.svg?react';
import PersonIcon from '../assets/icons/profile.svg?react';
import GroupIcon from '../assets/icons/group.svg?react';
import OneIcon from '../assets/icons/1.svg?react';
import TwoIcon from '../assets/icons/2.svg?react';
import ThreeIcon from '../assets/icons/3.svg?react';
import FourIcon from '../assets/icons/4.svg?react';
import FiveIcon from '../assets/icons/5.svg?react';
import SixIcon from '../assets/icons/6.svg?react';
import SevenIcon from '../assets/icons/7.svg?react';
import EightIcon from '../assets/icons/8.svg?react';
import NineIcon from '../assets/icons/9.svg?react';

export type SvgIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

// Curated icon set for intervention groups and interventions: 8 themed icons
// followed by the digits 1-9. The set is keyed by the existing InterventionIcon
// enum so stored data and every reader keep working without changes; the enum
// label is just an identifier here and does not describe the glyph. The
// previous 20 medical icons are no longer offered in the picker.
//
// Key order is the single source of truth for the picker order (the 8 themed
// icons, then digits 1-9): INTERVENTION_ICON_OPTIONS is derived from it below.
export const INTERVENTION_ICON_REGISTRY: Partial<Record<InterventionIcon, SvgIconComponent>> = {
  [InterventionIcon.Assessment]: ClipboardIcon,
  [InterventionIcon.MedicalConsultation]: ClinicIcon,
  [InterventionIcon.OccupationalTherapy]: HomeIcon,
  [InterventionIcon.Nursing]: BedIcon,
  [InterventionIcon.Psychology]: GlassesIcon,
  [InterventionIcon.OnlineSession]: HeadsetIcon,
  [InterventionIcon.IndividualExercise]: PersonIcon,
  [InterventionIcon.GroupExercise]: GroupIcon,
  [InterventionIcon.Physiotherapy]: OneIcon,
  [InterventionIcon.Hydrotherapy]: TwoIcon,
  [InterventionIcon.Education]: ThreeIcon,
  [InterventionIcon.SocialCounseling]: FourIcon,
  [InterventionIcon.Nutrition]: FiveIcon,
  [InterventionIcon.PainManagement]: SixIcon,
  [InterventionIcon.FatigueManagement]: SevenIcon,
  [InterventionIcon.WorkplaceRehab]: EightIcon,
  [InterventionIcon.Mindfulness]: NineIcon
};

// Fallback glyph for legacy data that holds an enum value no longer in the
// curated set (e.g. OTHER/SPORTS/CREATIVITY). New groups and interventions
// default to no icon (the empty option), so this is not used for fresh data.
export const DEFAULT_INTERVENTION_ICON: InterventionIcon = InterventionIcon.Assessment;

// Options offered in the picker, derived from the registry keys so there is a
// single source of truth. Enum values are all non-integer strings, so
// Object.keys preserves the insertion order defined above.
export const INTERVENTION_ICON_OPTIONS = Object.keys(
  INTERVENTION_ICON_REGISTRY
) as InterventionIcon[];
